import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Check, Sparkles, Tag, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Subscription = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState(null); // null, 'valid', 'invalid'
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        const response = await axios.get(`${API}/subscriptions/pricing`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPricing(response.data);
      } else {
        // Fallback to public pricing
        const response = await axios.get(`${API}/admin/public/pricing`);
        if (response.data && response.data.regions) {
          const defaultRegion = response.data.regions.find(r => r.region === response.data.default_region) 
            || response.data.regions[0];
          if (defaultRegion) {
            setPricing({
              currency: defaultRegion.currency,
              monthly_price: defaultRegion.monthly_price,
              yearly_price: defaultRegion.yearly_price
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
      // Use default pricing
      setPricing({
        currency: 'EUR',
        monthly_price: 9.99,
        yearly_price: 99.99
      });
    } finally {
      setLoadingPricing(false);
    }
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API}/subscriptions/validate-coupon`,
        { coupon_code: couponCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.valid) {
        setCouponStatus('valid');
        setCouponDiscount(response.data.discount_percent);
        toast.success(`Coupon valid! ${response.data.discount_percent}% discount`);
      }
    } catch (error) {
      setCouponStatus('invalid');
      setCouponDiscount(0);
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleSubscribe = async (plan) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API}/subscriptions/create-checkout`,
        { 
          plan,
          coupon_code: couponStatus === 'valid' ? couponCode : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.success(t('auth.couponApplied'));
        navigate('/app/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount) => {
    if (!pricing) return `€${amount}`;
    const symbol = pricing.currency === 'EUR' ? '€' : 
                   pricing.currency === 'USD' ? '$' : 
                   pricing.currency === 'GBP' ? '£' : 
                   pricing.currency === 'INR' ? '₹' : 
                   pricing.currency;
    return `${symbol}${amount.toFixed(2)}`;
  };

  const calculateDiscountedPrice = (originalPrice) => {
    if (couponStatus === 'valid' && couponDiscount > 0) {
      return originalPrice * (1 - couponDiscount / 100);
    }
    return originalPrice;
  };

  const monthlyPrice = pricing?.monthly_price || 9.99;
  const yearlyPrice = pricing?.yearly_price || 99.99;
  
  // Calculate actual savings for annual plan
  const annualSavings = (monthlyPrice * 12) - yearlyPrice;
  const savingsText = annualSavings > 0 ? `Save €${annualSavings.toFixed(2)}/year` : null;

  // Same features for both plans
  const allFeatures = [
    t('subscription.unlimitedPackages'),
    t('subscription.unlimitedBookings'),
    t('subscription.documentStorage'),
    t('subscription.realTimeAnalytics'),
    t('subscription.paymentTracking'),
    t('subscription.multiCurrencySupport'),
    t('subscription.emailSupport'),
    t('subscription.prioritySupport'),
    t('subscription.advancedReporting'),
    t('subscription.exportToPdfExcel')
  ];

  const plans = [
    {
      name: t('subscription.monthly'),
      originalPrice: monthlyPrice,
      price: calculateDiscountedPrice(monthlyPrice),
      period: '/' + t('subscription.perMonth').split(' ')[0],
      value: 'monthly',
      features: allFeatures,
      recommended: false
    },
    {
      name: t('subscription.annual'),
      originalPrice: yearlyPrice,
      price: calculateDiscountedPrice(yearlyPrice),
      period: '/' + t('subscription.perYear').split(' ')[0],
      value: 'annual',
      savings: savingsText,
      features: allFeatures,
      recommended: true
    }
  ];

  if (loadingPricing) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <Badge variant="secondary" className="mb-4">
            <Sparkles className="w-3 h-3 mr-1" />
            {t('subscription.firstTwoMonthsFree')}
          </Badge>
          <h1 className="text-5xl font-heading font-extrabold text-foreground mb-4">
            {t('subscription.chooseYourPlan')}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('subscription.startManaging')}
          </p>
        </div>

        {/* Coupon Code Input */}
        <div className="max-w-md mx-auto mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="w-4 h-4" />
                {t('subscription.haveCoupon') || 'Have a coupon code?'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Input 
                    placeholder={t('subscription.enterCoupon') || 'Enter coupon code'}
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponStatus(null);
                    }}
                    className={`font-mono ${
                      couponStatus === 'valid' ? 'border-green-500 pr-10' : 
                      couponStatus === 'invalid' ? 'border-red-500 pr-10' : ''
                    }`}
                  />
                  {couponStatus === 'valid' && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                  )}
                  {couponStatus === 'invalid' && (
                    <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                  )}
                </div>
                <Button 
                  variant="outline" 
                  onClick={validateCoupon}
                  disabled={!couponCode.trim() || validatingCoupon}
                >
                  {validatingCoupon ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('subscription.applyCoupon') || 'Apply'
                  )}
                </Button>
              </div>
              {couponStatus === 'valid' && (
                <p className="text-sm text-green-600 mt-2">
                  ✓ {couponDiscount}% {t('subscription.discountApplied') || 'discount will be applied'}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          {plans.map((plan) => (
            <Card
              key={plan.value}
              className={`relative ${
                plan.recommended
                  ? 'border-primary shadow-2xl scale-105'
                  : 'border-border'
              }`}
            >
              {plan.recommended && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground px-4 py-1">
                    {t('subscription.recommended')}
                  </Badge>
                </div>
              )}
              <CardHeader className="text-center pt-8">
                <CardTitle className="text-2xl font-heading font-semibold">{plan.name}</CardTitle>
                {plan.savings && (
                  <Badge className="w-fit mx-auto mt-2 bg-green-600 text-white hover:bg-green-700">
                    🎉 {plan.savings}
                  </Badge>
                )}
                <div className="mt-4">
                  {couponStatus === 'valid' && couponDiscount > 0 ? (
                    <>
                      <span className="text-2xl text-muted-foreground line-through">
                        {formatPrice(plan.originalPrice)}
                      </span>
                      <span className="text-5xl font-extrabold text-green-600 ml-2">
                        {formatPrice(plan.price)}
                      </span>
                    </>
                  ) : (
                    <span className="text-5xl font-extrabold text-foreground">
                      {formatPrice(plan.originalPrice)}
                    </span>
                  )}
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-primary" />
                      </div>
                      <span className="text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={plan.recommended ? 'default' : 'outline'}
                  size="lg"
                  onClick={() => handleSubscribe(plan.value)}
                  disabled={loading}
                >
                  {loading ? t('common.loading') : t('subscription.selectPlan')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bank Transfer Option */}
        <div className="text-center">
          <Card className="max-w-xl mx-auto">
            <CardHeader>
              <CardTitle className="text-lg">{t('subscription.bankTransferOption')}</CardTitle>
              <CardDescription>
                {t('subscription.bankTransferInstructions')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" asChild>
                <a href="mailto:sales@2ds.fi">{t('subscription.contactForBankTransfer')}</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
